import { useNavigate } from 'react-router-dom'
import { buttonClasses } from './control.js'
/**
 * The layout shell every screen sits in. One column, thumb-reachable, capped
 * width so a tablet or a desktop QA session still reads like the phone build.
 */
/**
 * THE THREE BANDS — AD-3 §3. NavBar, Body, ActionBar.
 *
 * WHY A dvh FLEX COLUMN AND NOT position:fixed. The spec says the bands are
 * fixed, and what that has to mean is: they never scroll and they never cover
 * content. Fixed bars deliver the first by construction and the second only if
 * the body carries a padding that exactly matches their height — and that
 * padding is a number someone has to keep correct as the button grows with her
 * text size. F7 is exactly that arithmetic having drifted: 31px of question
 * text hidden behind an opaque bar.
 *
 * A flex column with a scrolling middle gives the identical result and makes
 * §7.6 true STRUCTURALLY. There is no height to keep in sync, so there is
 * nothing to drift, and the bands stay correct at 200% text where a hardcoded
 * 120px would not.
 *
 * BODY HAS TWO DISPOSITIONS AND NOTHING ELSE (§3.1), chosen by screen KIND and
 * never by content length:
 *
 *   content  top-aligned — items, documents, forms, grids
 *   status   centred     — home, completions, paused, install, notifications
 *
 * That is the whole answer to F1, and it holds at both extremes: a one-item
 * beep top-aligns and the space falls below the answer group, bounded, reading
 * as margin; a 25-row grid top-aligns and simply overflows into the scroll.
 * Nothing fills the empty half — it gets an edge.
 *
 * SAFETY SURFACES ARE EXEMPT FROM ALL OF THIS (§3.1) and keep their own
 * composition. They pass no nav and no action, so they render as a bare
 * scrolling column. The prototype that applied the eyebrow rule globally turned
 * "Support is available" into an uppercase eyebrow; scoping is what prevents
 * that, and it is by omission rather than by a flag.
 */
export function Screen({
  children,
  support = false,
  stop = false,
  // { left, right } — the navigation control and the progress count.
  nav = null,
  // The screen's ONE forward action. Lives in the ActionBar.
  action = null,
  /*
   * One line above the primary, inside the band. The band grows by exactly
   * this on request and no more — a band whose height moves with state is not
   * a fixed position, which is the whole argument §4.6 rests on.
   */
  notice = null,
  /*
   * §5 — a route's own control in the footnote row. Consent's `Decline` is the
   * case this exists for: it is an action of standing that is NOT the way
   * forward, so it cannot be the primary, and it must not become a secondary
   * sitting beside the primary where the two read as a choice of equals.
   */
  footnote = null,
  disposition = 'content',
}) {
  const hasFootnote = support || stop || Boolean(footnote)
  const hasBar = Boolean(action) || hasFootnote || Boolean(notice)

  return (
    /*
     * fixed inset-0, not h-dvh in flow. AD-5 defect 1.
     *
     * h-dvh made the shell the right SIZE but left it in the document's flow,
     * so anything inside that escaped normal layout could still extend the
     * document — and the NavBar and ActionBar live outside <main>, so a drag on
     * either scrolled the whole app away to y = -97 and left a blank page.
     *
     * Taking the shell out of flow entirely means the document has no content
     * to scroll, whatever a descendant does. <main> stays the only scroll
     * container, which is what the band architecture claims and now cannot be
     * argued out of by a stray absolutely-positioned element.
     */
    <div className="fixed inset-0 flex flex-col bg-[var(--surface)]">
      {nav ? (
        <header
          className="flex-none border-b border-[var(--line)] bg-[var(--surface)]"
          style={{ paddingTop: 'var(--safe-top)' }}
        >
          {/*
            44px row, never taller, never conditional on internal pagination.
            F5's Back-appears-on-screen-2-of-a-part is what a conditional slot
            produces.
          */}
          <div className="mx-auto flex min-h-[44px] w-full max-w-[34em] items-center justify-between px-[var(--s-4)]">
            <div className="flex min-w-0 items-center">{nav.left ?? null}</div>
            {/* Progress is a COUNT and never a bar: at 4 of 221 a 3px rule
                renders as a 6px blue tick that reads as a rendering fault. */}
            <div className="t-eyebrow shrink-0 tabular-nums">{nav.right ?? null}</div>
          </div>
        </header>
      ) : null}

      <main
        className={
          'min-h-0 flex-1 overflow-y-auto ' +
          (disposition === 'status' ? 'flex flex-col justify-center' : '')
        }
      >
        <div
          className="mx-auto w-full max-w-[34em] px-[var(--s-4)] py-[var(--s-6)]"
          style={nav ? undefined : { paddingTop: 'calc(var(--safe-top) + var(--s-6))' }}
        >
          {children}
        </div>
      </main>

      {hasBar ? (
        <div
          className="flex-none border-t border-[var(--line)] bg-[var(--surface)]"
          style={{
            padding: 'var(--s-3) var(--s-4)',
            paddingBottom: 'calc(var(--s-3) + var(--safe-bottom))',
          }}
        >
          <div className="mx-auto w-full max-w-[34em]">
            {notice}
            {action}
            {hasFootnote ? (
              /*
                The footnote row carries both unconditional affordances, and
                being in the BAND is the point: DS-2 §4 requires the support
                affordance on every answering screen, and it used to sit below
                the sticky bar at the bottom of a page that can be 3,000px long
                — so on a long grid it could not be reached without scrolling to
                the end of the instrument.
              */
              <div
                /*
                 * WRAPS RATHER THAN OVERFLOWS. At 200% text "Stop for now"
                 * measures 166px against an 88px share of the row, so two
                 * quiet links side by side could not fit and the band reached
                 * 241px — 29% of the viewport.
                 *
                 * flex-wrap is the stacking threshold, and it is a threshold
                 * rather than a breakpoint: they sit side by side while they
                 * fit and stack the moment they do not, at whatever text size
                 * that happens to be. min-h rather than h so the row is one
                 * line normally and exactly two when stacked — bounded either
                 * way, and never clipped.
                 *
                 * MEASURED, BECAUSE IT WAS RAISED AS A DEFECT AND IS NOT ONE.
                 * The concern was that `justify-between` gives each link half
                 * the row (~163px at 200%) and both then wrap mid-phrase. It
                 * does not: with `flex-wrap`, a line is BROKEN before its items
                 * are shrunk, so two links that cannot share a line each take
                 * their own at full width and never squeeze. Measured on the
                 * real two-link row —
                 *
                 *   root 16  side by side   1 line each   row 44px
                 *   root 20  stacked        1 line each   row 110px
                 *   root 24  stacked        1 line each   row 132px
                 *   root 32  stacked        1 / 2 lines   row 210px, no overflow
                 *
                 * — where the only wrap left is "Support and helplines" needing
                 * 342px against a 326px row at 200%, which no layout rule can
                 * fix and stacking already gives the most room possible.
                 *
                 * Adding `shrink-0`/`max-w-full` to force it changes nothing at
                 * any size; it was tried and reverted rather than shipped as an
                 * inert fix with a comment claiming otherwise.
                 */
                className={
                  'flex min-h-[max(44px,2.75rem)] flex-wrap items-center gap-x-[var(--s-3)] ' +
                  (action ? 'mt-[var(--s-1)] ' : '') +
                  ((stop || footnote) && support ? 'justify-between' : (stop || footnote) ? 'justify-start' : 'justify-end')
                }
              >
                {footnote ?? (stop ? <StopForNow /> : null)}
                {support ? <SupportFootnote /> : null}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  )
}

/**
 * STOP FOR NOW — the deliberate neutral exit (DS-5 §7.4, DS-3 B2).
 *
 * What actually helps someone whose phone is monitored is not a warning; it is
 * being able to leave the app in a neutral state DELIBERATELY, without that act
 * looking like anything. A warning helps almost nobody and frightens everybody.
 *
 * IT NEEDS NO EXPLANATION AND GETS NONE. "Stop for now" reads as a pause.
 * Someone who needs it will use it; someone who does not will never notice it.
 * Exactly the same move as the permanent support affordance: make the thing
 * unconditional and unremarkable so that using it discloses nothing. An exit
 * that appeared only when it was needed would mark the person who needed it.
 *
 * It LANDS ON A NEUTRAL SCREEN rather than leaving the item on display, which
 * is the whole point — the alternative is an exit that leaves her disclosure on
 * screen for whoever picks the phone up next. `replace` so a back-swipe cannot
 * bring the item back either.
 *
 * Nothing is saved or discarded here: answers upsert per item as she gives
 * them, so there is nothing in flight for this to commit.
 */
function StopForNow() {
  const navigate = useNavigate()
  return (
    <>
      <button type="button" onClick={() => navigate('/paused', { replace: true })} className={buttonClasses('quiet')}>
        Stop for now
      </button>
    </>
  )
}

/**
 * THE PERMANENT SUPPORT AFFORDANCE — DS-1 part 2 rec B, DS-2 §4.
 *
 * A quiet, persistent, non-modal route to the support screen, on every
 * answering screen and on home, FOR EVERY PARTICIPANT AT ALL TIMES, regardless
 * of what anyone has answered.
 *
 * UNCONDITIONAL IS THE ENTIRE POINT, and it does three things at once:
 *
 *  1. It removes the signal. When an interposed safety screen is NOT shown,
 *     nothing has been withdrawn — the resources are exactly where they always
 *     are. That is what lets the interposition stop meaning "your answer was
 *     the serious one".
 *  2. It never discloses. An affordance that appears BECAUSE you answered 2 is
 *     a reaction to the value of an answer, visible to anyone looking over a
 *     shoulder. One that is always there tells no one anything.
 *  3. It is there on the check-in where she did not cross a threshold and
 *     needed it anyway — which nothing else in this app can do. It also fixes
 *     the older hole where a dismissed support screen could never be reached
 *     again, from anywhere, ever.
 *
 * So it takes no props, reads no state, and has no conditional branch. There is
 * deliberately nothing here that COULD vary with an answer.
 *
 * It lives in the shell rather than in any screen, which is what makes it
 * survive a crash: BatteryBoundary renders <Screen support> too, so a boundary
 * firing over a safety surface cannot replace crisis resources with an error
 * message for someone who has just disclosed (DS-2 §3.11).
 *
 * LABEL: design specified the FORM and explicitly declined to write the words —
 * it must be a signpost, never a question. "Support and helplines" is design's
 * own stated example of the correct form; "Need help right now?" is a judgement
 * addressed to the participant. Using their example rather than inventing one,
 * and flagged to the operator as theirs to confirm.
 */
function SupportFootnote() {
  return (
    <>
      <a href="/support" className={buttonClasses('quiet')}>
        Support and helplines
      </a>
    </>
  )
}

// One states table, one definition, every control.
/**
 * The title of a screen that has NO question on it — AD-3 §2.2, and the whole
 * of F2. DS-2's "the question is the largest thing" is a rule about ITEM
 * screens; it was being applied to seven screens containing no question, which
 * is why they all had a 16px title with nothing above it.
 *
 * `subtitle` stays --t-meta. It is NOT an eyebrow: the eyebrow is a task name
 * that sits ABOVE a question, and using it here is the mistake that turned
 * "Support is available" into uppercase chrome on a crisis screen.
 */
export function ScreenTitle({ children, subtitle }) {
  return (
    <header className="mb-[var(--s-5)]">
      <h1 className="t-title text-[var(--ink-900)]">{children}</h1>
      {subtitle ? <p className="t-meta mt-[var(--s-1)]">{subtitle}</p> : null}
    </header>
  )
}

/**
 * The one primary action on a screen. Full width, thumb height, no animation.
 *
 * Enabled: white on blue-700, measured at 6.83:1 against a 4.5:1 floor.
 *
 * Disabled is NOT a dimmed version of the same solid button. It was
 * slate-500 on slate-300 — 3.2:1, below the floor, and worse, it still looked
 * like a real filled control, so it invited a tap that did nothing. The
 * disabled state is load-bearing in this app: it is how the no-skip rule tells
 * a participant they are not finished, so it has to be both legible and
 * obviously not-yet-pressable. An outline reads as "not ready" where a grey
 * fill reads as "broken".
 */
export function PrimaryButton({
  children,
  disabled,
  incomplete,
  onClick,
  type = 'button',
  /*
   * FORWARDED, and it is load-bearing since AD-3 §3.
   *
   * The primary now lives in the ActionBar, OUTSIDE the <form> it submits, so
   * it is associated by the `form` attribute instead of by containment. This
   * component destructured a fixed prop list and silently dropped anything
   * else — so the attribute never reached the DOM, the button submitted
   * nothing, and every form in the app stopped validating.
   *
   * Nothing threw and every screen still looked right; the walk caught it by
   * pressing the button and finding no error where an invalid form should have
   * produced one. That is the whole argument for driving the app rather than
   * reading it.
   */
  form,
}) {
  return (
    <button
      type={type}
      form={form}
      onClick={onClick}
      /*
       * `incomplete` NO LONGER SETS aria-disabled, and removing it is the
       * point of AD-4 §4.3.
       *
       * When the button LOOKED disabled, aria-disabled was the honest label for
       * it and DS-2 §3.8 kept it focusable so it could still be pressed. The
       * primary is now always live and always explains — pressing it on an
       * incomplete screen reveals the outstanding note — so announcing it as
       * disabled tells assistive tech the opposite of what is true, and hides
       * the one control that would tell her what is missing.
       *
       * The walk caught this: Playwright treats aria-disabled as not-enabled
       * and refused to click, which is exactly what a screen reader user would
       * have been told.
       *
       * `data-incomplete` stays as the machine-readable state for tests.
       */
      disabled={disabled}
      data-incomplete={incomplete ? '1' : undefined}
      className={buttonClasses('primary', { incomplete })}
    >
      {children}
    </button>
  )
}

export function SecondaryButton({ children, disabled, onClick, type = 'button', form }) {
  return (
    <button
      type={type}
      form={form}
      onClick={onClick}
      disabled={disabled}
      className={buttonClasses('secondary')}
    >
      {children}
    </button>
  )
}

/** One error presentation for the whole app. Never clinical, never alarming. */
export function ErrorNote({ error }) {
  if (!error) return null
  const message =
    typeof error === 'string' ? error : error.message || 'That did not go through.'
  return (
    <p role="alert" className="mt-3 rounded-[var(--r-control)] bg-[var(--critical-tint)] px-3 py-2 text-sm text-[var(--critical)]">
      {message}
    </p>
  )
}
